import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Copy, Key, X } from 'lucide-react';

import { api } from '@/lib/api/client';
import { Button } from '@/components/ui/Button';
import { toast } from '@/components/ui/Toast';
import { useT } from '@/lib/i18n/useT';

interface MintResponse {
  id: number;
  rawToken: string;
  expiresAt: string | null;
}

/**
 * Caso 11 v3 — Compact button + one-shot reveal modal for station access
 * tokens. Mints a fresh token on click and shows the URL ONCE; closing
 * the modal scrubs the secret from local state.
 *
 * The URL hits `/operator/:stationId?stationToken=<raw>`. The actual auth
 * exchange (turning the token into an operator session) is v4 work — for
 * v3 the URL is just a sharable handle.
 */
export function StationAccessTokenButton({
  stationId,
}: {
  stationId: number;
}) {
  const t = useT();
  const [revealed, setRevealed] = useState<MintResponse | null>(null);

  const mint = useMutation({
    mutationFn: () =>
      api.post<MintResponse>(`/xk-station/${stationId}/access-tokens`, {
        // Default 7-day TTL on the backend; label is optional.
      }),
    onSuccess: (data) => setRevealed(data),
    onError: (err: any) =>
      toast({
        tone: 'error',
        title: t('stations.detail.tokenMintFailed'),
        description: err?.message,
      }),
  });

  function operatorUrl(rawToken: string) {
    // v4 — point at the operator-login route which exchanges the token
    // for a session before redirecting to /operator/:stationId. The
    // station id rides along for the rare case the exchange fails
    // server-side (we still know which station the link was for).
    return `${window.location.origin}/operator-login?stationToken=${rawToken}&stationId=${stationId}`;
  }

  async function copyUrl() {
    if (!revealed) return;
    try {
      await navigator.clipboard.writeText(operatorUrl(revealed.rawToken));
      toast({ tone: 'success', title: t('stations.detail.tokenCopied') });
    } catch {
      toast({
        tone: 'error',
        title: t('stations.detail.tokenCopyFailed'),
      });
    }
  }

  return (
    <>
      <Button
        variant="ghost"
        leftIcon={<Key className="h-3.5 w-3.5" />}
        onClick={() => mint.mutate()}
        loading={mint.isPending}
      >
        {t('stations.detail.generateOperatorLink')}
      </Button>

      {revealed && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
        >
          <div className="w-full max-w-lg rounded-2xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-5 shadow-2xl">
            <header className="mb-3 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-[var(--color-fg-primary)]">
                  {t('stations.detail.tokenReady')}
                </h2>
                <p className="mt-1 text-xs text-[var(--color-fg-muted)]">
                  {t('stations.detail.tokenWarning')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setRevealed(null)}
                aria-label={t('common.close')}
                className="rounded p-1 hover:bg-[var(--color-bg-elevated)]"
              >
                <X className="h-4 w-4" />
              </button>
            </header>

            <div className="rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] p-3 font-mono text-xs break-all">
              {operatorUrl(revealed.rawToken)}
            </div>

            {revealed.expiresAt && (
              <p className="mt-2 text-xs text-[var(--color-fg-muted)]">
                {t('stations.detail.tokenExpiresAt', {
                  when: new Date(revealed.expiresAt).toLocaleString(),
                })}
              </p>
            )}

            <div className="mt-3 flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => setRevealed(null)}
              >
                {t('common.close')}
              </Button>
              <Button
                leftIcon={<Copy className="h-4 w-4" />}
                onClick={copyUrl}
              >
                {t('stations.detail.tokenCopy')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
